export const MAGNIFY_LEVELS = [1 / 1, 1 / 1.5, 1 / 3] as const;

export const calculateMagnifyDimensions = (
  element: HTMLImageElement | null,
  { x, y }: { x: number; y: number },
  magnifyFraction: number = MAGNIFY_LEVELS[0],
) => {
  const rect = element?.getBoundingClientRect();
  const containerWidth = Number(rect?.width);
  const containerHeight = Number(rect?.height);

  // Use natural dimensions to compute the actual rendered size under object-contain.
  // Fallback to container dimensions if the image hasn't loaded yet.
  const naturalWidth = element?.naturalWidth || containerWidth;
  const naturalHeight = element?.naturalHeight || containerHeight;

  const containerAspect =
    containerHeight > 0 ? containerWidth / containerHeight : 1;
  const imageAspect = naturalHeight > 0 ? naturalWidth / naturalHeight : 1;

  // Compute the actual rendered image bounds, accounting for object-contain letterboxing.
  let renderedWidth: number;
  let renderedHeight: number;
  let offsetX: number;
  let offsetY: number;

  if (imageAspect > containerAspect) {
    // Image is wider than container — constrained by width, pillarboxed vertically.
    renderedWidth = containerWidth;
    renderedHeight = containerWidth / imageAspect;
    offsetX = 0;
    offsetY = (containerHeight - renderedHeight) / 2;
  } else {
    // Image is taller than container — constrained by height, letterboxed horizontally.
    renderedHeight = containerHeight;
    renderedWidth = containerHeight * imageAspect;
    offsetX = (containerWidth - renderedWidth) / 2;
    offsetY = 0;
  }

  // Magnifier covers magnifyFraction of the rendered image in each dimension, maintaining aspect ratio.
  const magnifyWidth = magnifyFraction * renderedWidth;
  const magnifyHeight = magnifyFraction * renderedHeight;

  // Enlarged popup: fit within half the screen on the longer side, maintaining aspect ratio.
  const maxDimension = Math.round((window.innerWidth / 2 - 80) * 1.25);
  let enlargedWidth: number;
  let enlargedHeight: number;

  if (imageAspect >= 1) {
    enlargedWidth = maxDimension;
    enlargedHeight = Math.round(maxDimension / imageAspect);
  } else {
    enlargedHeight = maxDimension;
    enlargedWidth = Math.round(maxDimension * imageAspect);
  }

  // Scale from rendered thumbnail to popup (consistent across both axes since aspect ratios match).
  const scale = enlargedHeight / magnifyHeight;

  // Background position: center the mouse position (within image content) in the popup.
  const xInImage = x - offsetX;
  const yInImage = y - offsetY;
  const enlargedPositionX = Math.round(enlargedWidth / 2 - xInImage * scale);
  const enlargedPositionY = Math.round(enlargedHeight / 2 - yInImage * scale);

  return {
    magnifier: {
      height: magnifyHeight,
      width: magnifyWidth,
    },
    image: {
      width: renderedWidth,
      height: renderedHeight,
      offsetX,
      offsetY,
    },
    enlargedImage: {
      height: enlargedHeight,
      width: enlargedWidth,
      backgroundSize: {
        x: Math.round(renderedWidth * scale),
        y: Math.round(renderedHeight * scale),
      },
      backgroundPosition: {
        x: enlargedPositionX,
        y: enlargedPositionY,
      },
    },
  };
};
